"use client";

import { cn } from "@gigpro/ui/tailwind/cn";
import { Select as SelectPrimitive } from "radix-ui";
import type * as React from "react";
import { ChevronDown } from "../../Icons";
import { Label } from "../Label";
import { usePortalScope } from "../PortalScope";

const SelectRoot = SelectPrimitive.Root;

const SelectValue = SelectPrimitive.Value;

export interface SelectLabelProps extends React.ComponentProps<typeof Label> {}
const SelectLabel = ({ ref, className, children, ...props }: SelectLabelProps) => (
  <Label ref={ref} className={cn("text-normal font-semibold", className)} {...props}>
    {children}
  </Label>
);
SelectLabel.displayName = "SelectLabel";

const SelectBaseTrigger = SelectPrimitive.Trigger;

export interface SelectTriggerProps extends React.ComponentProps<typeof SelectPrimitive.Trigger> {
  invalid?: boolean;
}
const SelectTrigger = ({ ref, className, children, invalid, ...props }: SelectTriggerProps) => (
  <SelectBaseTrigger
    ref={ref}
    className={cn(
      "group/select flex w-full items-center justify-between rounded-sm border-2 border-medium-gray bg-white px-3 py-2 text-large/5 text-black transition-colors focus-within:border-brand focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-placeholder:text-medium-gray data-[state=open]:border-brand",
      invalid && "border-error",
      className,
    )}
    {...props}
  >
    <span className="truncate transition-opacity group-data-[state=open]/select:opacity-0">{children}</span>
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="size-4 shrink-0 text-brand transition-transform group-data-[state=open]/select:rotate-180" />
    </SelectPrimitive.Icon>
  </SelectBaseTrigger>
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export interface SelectContentProps extends React.ComponentProps<typeof SelectPrimitive.Content> {
  viewportClassName?: string;
}
const SelectContent = ({
  ref,
  className,
  children,
  position = "popper",
  viewportClassName,
  ...props
}: SelectContentProps) => {
  const scope = usePortalScope();
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 min-w-[8rem] overflow-hidden rounded-sm bg-white text-black shadow-back data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...scope}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "max-h-60 space-y-1 p-1",
            position === "popper" && "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)",
            viewportClassName,
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};
SelectContent.displayName = SelectPrimitive.Content.displayName;

export interface SelectItemProps extends React.ComponentProps<typeof SelectPrimitive.Item> {}
const SelectItem = ({ ref, className, children, ...props }: SelectItemProps) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default items-center rounded-sm px-2 py-1.5 text-large outline-none select-none focus:bg-background focus:text-black data-disabled:pointer-events-none data-disabled:opacity-50 data-[state=checked]:bg-background data-[state=checked]:font-semibold [&>span]:w-full",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
);
SelectItem.displayName = SelectPrimitive.Item.displayName;

export interface SelectSeparatorProps extends React.ComponentProps<typeof SelectPrimitive.Separator> {}
const SelectSeparator = ({ ref, className, ...props }: SelectSeparatorProps) => (
  <SelectPrimitive.Separator ref={ref} className={cn("-mx-1 my-1 h-px bg-light-gray", className)} {...props} />
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export interface SelectProps
  extends SelectPrimitive.SelectProps, Omit<SelectPrimitive.SelectTriggerProps, "value" | "defaultValue" | "dir"> {
  ref?: React.Ref<React.ComponentRef<typeof SelectTrigger>>;
  className?: string;
  label?: React.ReactNode;
  placeholder?: React.ReactNode;
  viewportClassName?: string;
}
const Select = ({
  ref,
  className,
  children,
  label,
  placeholder,
  value,
  defaultValue,
  defaultOpen,
  onValueChange,
  open,
  onOpenChange,
  dir,
  name,
  autoComplete,
  disabled,
  required,
  viewportClassName,
  ...triggerProps
}: SelectProps) => {
  const rootProps: SelectPrimitive.SelectProps = {
    value,
    defaultValue,
    defaultOpen,
    onValueChange,
    open,
    onOpenChange,
    dir,
    name,
    autoComplete,
    disabled,
    required,
  };
  return (
    <SelectPrimitive.Root {...rootProps}>
      <div className={cn("flex flex-col gap-0.5", className)}>
        {label && <SelectLabel>{label}</SelectLabel>}
        <SelectTrigger ref={ref} {...triggerProps}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="-mt-1" viewportClassName={viewportClassName}>
          {children}
        </SelectContent>
      </div>
    </SelectPrimitive.Root>
  );
};
Select.displayName = SelectPrimitive.Root.displayName;

export {
  Select,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectRoot,
  SelectBaseTrigger,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
export { Select as SelectPrimitive } from "radix-ui";
